import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Pause } from 'lucide-react';
import './App.css';

const VideoWithReflections = ({ src, className = '', videoRef, ...videoProps }) => {
  const mainVideoRef = useRef(null);
  const topReflectionRef = useRef(null);
  const bottomReflectionRef = useRef(null);

  const assignMainVideoRef = useCallback(
    (node) => {
      mainVideoRef.current = node;
      if (!videoRef) return;
      if (typeof videoRef === 'function') {
        videoRef(node);
      } else {
        videoRef.current = node;
      }
    },
    [videoRef]
  );

  useEffect(() => {
    let rafId = null;
    let syncLoopId = null;
    let teardown = null;

    const attemptSetup = () => {
      const mainVideo = mainVideoRef.current;
      const topReflection = topReflectionRef.current;
      const bottomReflection = bottomReflectionRef.current;

      if (!mainVideo || !topReflection || !bottomReflection) {
        rafId = requestAnimationFrame(attemptSetup);
        return;
      }

      const reflections = [topReflection, bottomReflection];

      reflections.forEach((reflection) => {
        reflection.muted = true;
        reflection.playsInline = true;
        reflection.disablePictureInPicture = true;
        reflection.setAttribute('tabindex', '-1');
        reflection.setAttribute('aria-hidden', 'true');
      });

      const syncCurrentTime = () => {
        const currentTime = mainVideo.currentTime || 0;
        reflections.forEach((reflection) => {
          if (!reflection || reflection.readyState < 2) return;
          const diff = Math.abs((reflection.currentTime || 0) - currentTime);
          if (diff > 0.15) {
            try {
              reflection.currentTime = currentTime;
            } catch (err) {
              /* ignore occasional DOMException while seeking */
            }
          }
        });
      };

      const playReflections = () => {
        syncCurrentTime();
        reflections.forEach((reflection) => {
          const promise = reflection.play();
          if (promise && typeof promise.catch === 'function') {
            promise.catch(() => {});
          }
        });

        if (!syncLoopId) {
          const step = () => {
            syncCurrentTime();
            syncLoopId = requestAnimationFrame(step);
          };
          syncLoopId = requestAnimationFrame(step);
        }
      };

      const pauseReflections = () => {
        reflections.forEach((reflection) => {
          reflection.pause();
        });
        if (syncLoopId) {
          cancelAnimationFrame(syncLoopId);
          syncLoopId = null;
        }
      };

      const handleLoaded = () => syncCurrentTime();

      mainVideo.addEventListener('play', playReflections);
      mainVideo.addEventListener('pause', pauseReflections);
      mainVideo.addEventListener('ended', pauseReflections);
      mainVideo.addEventListener('seeking', syncCurrentTime);
      mainVideo.addEventListener('seeked', syncCurrentTime);
      mainVideo.addEventListener('timeupdate', syncCurrentTime);
      mainVideo.addEventListener('loadeddata', handleLoaded);
      mainVideo.addEventListener('loadedmetadata', handleLoaded);

      handleLoaded();

      if (!mainVideo.paused) {
        playReflections();
      }

      teardown = () => {
        pauseReflections();
        mainVideo.removeEventListener('play', playReflections);
        mainVideo.removeEventListener('pause', pauseReflections);
        mainVideo.removeEventListener('ended', pauseReflections);
        mainVideo.removeEventListener('seeking', syncCurrentTime);
        mainVideo.removeEventListener('seeked', syncCurrentTime);
        mainVideo.removeEventListener('timeupdate', syncCurrentTime);
        mainVideo.removeEventListener('loadeddata', handleLoaded);
        mainVideo.removeEventListener('loadedmetadata', handleLoaded);
      };
    };

    attemptSetup();

    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      if (teardown) {
        teardown();
      }
    };
  }, [src]);

  return (
    <div className="video-reflection-frame">
      <div className="video-reflection video-reflection--top">
        <video
          ref={topReflectionRef}
          className="video-reflection__video video-reflection__video--top"
          playsInline
          muted
          autoPlay
          preload="auto"
        >
          <source src={src} type="video/mp4" />
        </video>
      </div>
      <video
        ref={assignMainVideoRef}
        className={`background-video ${className}`.trim()}
        {...videoProps}
      >
        <source src={src} type="video/mp4" />
      </video>
      <div className="video-reflection video-reflection--bottom">
        <video
          ref={bottomReflectionRef}
          className="video-reflection__video video-reflection__video--bottom"
          playsInline
          muted
          autoPlay
          preload="auto"
        >
          <source src={src} type="video/mp4" />
        </video>
      </div>
    </div>
  );
};

function App() {
  const [currentPage, setCurrentPage] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showTransition, setShowTransition] = useState(false);
  // Removido overlay de logo do primeiro vídeo a pedido do usuário
  const [showArticleModal, setShowArticleModal] = useState(false);
  const audioRef = useRef(null);
  const video1Ref = useRef(null);
  const video2Ref = useRef(null);
  const logoVarsRef = useRef({ y: 78, offset: 4, shiftX: 0 });
  const spotifyCardRef = useRef(null);
  const articleCardRef = useRef(null);

  // Equalizar altura dos dois cards laterais na página final
  useEffect(() => {
    if (currentPage !== 4) return; // só na página final
    function syncHeights() {
      if (!spotifyCardRef.current || !articleCardRef.current) return;
      // Limpa antes para recalcular em resize responsivo
      articleCardRef.current.style.height = '';
      const targetHeight = spotifyCardRef.current.offsetHeight;
      if (targetHeight > 0) {
        articleCardRef.current.style.height = targetHeight + 'px';
        articleCardRef.current.classList.add('is-equalized');
      }
    }
    // pequena espera para garantir fontes carregadas / layout estável
    const raf = requestAnimationFrame(syncHeights);
    window.addEventListener('resize', syncHeights);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', syncHeights);
      if (articleCardRef.current) {
        articleCardRef.current.style.height = '';
        articleCardRef.current.classList.remove('is-equalized');
      }
    };
  }, [currentPage]);

  // Page 1: Jesus name animation with 10-second timer
  useEffect(() => {
    if (currentPage === 1 && isPlaying) {
      const timer = setTimeout(() => {
        setShowTransition(true);
        setTimeout(() => {
          setCurrentPage(2);
          setShowTransition(false);
        }, 1000);
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [currentPage, isPlaying]);

  // Page 2: Primeiro vídeo (sem logo overlay agora)
  useEffect(() => {
    if (currentPage === 2 && video1Ref.current) {
      const video = video1Ref.current;
      const handleVideoEnd = () => {
        setShowTransition(true);
        setTimeout(() => {
          setCurrentPage(3);
          setShowTransition(false);
        }, 1000);
      };
      video.addEventListener('ended', handleVideoEnd);
      video.play();
      return () => video.removeEventListener('ended', handleVideoEnd);
    }
  }, [currentPage]);

  // Keyboard fine‑tuning for logo position (only on page 2)
  useEffect(() => {
    if (currentPage !== 2) return;
    const handleKey = (e) => {
      let changed = false;
      if (e.key === 'g' || e.key === 'G') {
        document.body.classList.toggle('show-logo-guide');
      }
      if (e.key === 'ArrowUp') {
        logoVarsRef.current.y -= e.shiftKey ? 1 : 0.25;
        changed = true;
      } else if (e.key === 'ArrowDown') {
        logoVarsRef.current.y += e.shiftKey ? 1 : 0.25;
        changed = true;
      } else if (e.key === 'ArrowLeft') {
        logoVarsRef.current.shiftX -= e.shiftKey ? 1 : 0.25; // deslocamento horizontal
        changed = true;
      } else if (e.key === 'ArrowRight') {
        logoVarsRef.current.shiftX += e.shiftKey ? 1 : 0.25;
        changed = true;
      } else if (e.key === 'PageUp') { // ajuste fino offset vertical adicional
        logoVarsRef.current.offset -= e.shiftKey ? 1 : 0.25;
        changed = true;
      } else if (e.key === 'PageDown') {
        logoVarsRef.current.offset += e.shiftKey ? 1 : 0.25;
        changed = true;
      }
      if (changed) {
        const root = document.documentElement;
        root.style.setProperty('--logo-pos-y', logoVarsRef.current.y + '%');
        root.style.setProperty('--logo-offset-y', logoVarsRef.current.offset + '%');
        root.style.setProperty('--logo-shift-x', logoVarsRef.current.shiftX + '%');
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [currentPage]);

  // Page 3: Second video with final transition
  useEffect(() => {
    if (currentPage === 3 && video2Ref.current) {
      const video = video2Ref.current;
      
      const handleVideoEnd = () => {
        setShowTransition(true);
        setTimeout(() => {
          setCurrentPage(4);
          setShowTransition(false);
        }, 1000);
      };

      video.addEventListener('ended', handleVideoEnd);
      video.play();

      return () => {
        video.removeEventListener('ended', handleVideoEnd);
      };
    }
  }, [currentPage]);

  const handlePlay = () => {
    setIsPlaying(true);
    setCurrentPage(1);
    if (audioRef.current) {
      const audio = audioRef.current;
      const startAt = 5; // segundo inicial desejado
      const playAudio = () => {
        try {
          if (audio.duration && startAt < audio.duration) {
            audio.currentTime = startAt;
          }
          audio.play();
        } catch (e) {
          console.warn('Não foi possível iniciar áudio no tempo específico imediatamente:', e);
          audio.play();
        }
      };
      if (audio.readyState >= 1) {
        playAudio();
      } else {
        const onMeta = () => {
          playAudio();
          audio.removeEventListener('loadedmetadata', onMeta);
        };
        audio.addEventListener('loadedmetadata', onMeta);
        audio.load();
      }
    }
  };

  const handlePause = () => {
    setIsPlaying(false);
    setCurrentPage(0);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
  };

  return (
    <div className="app">
      <audio ref={audioRef} src="/Elevai-me (Edit).wav" />
      
      {/* Light Transition Overlay */}
      <AnimatePresence>
        {showTransition && (
          <motion.div
            className="light-transition"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1 }}
          />
        )}
      </AnimatePresence>

      {/* Page 0: Initial Play Button */}
      <AnimatePresence>
        {currentPage === 0 && (
          <motion.div 
            className="initial-page"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="initial-content">
              <div className="play-card">
                <div className="card-title-container">
                  <span className="card-title-word">TESTEMUNHO</span>
                  <span className="card-title-word">FÉ</span>
                  <span className="card-title-word">TRANSFORMAÇÃO</span>
                </div>

                <button
                  className="main-play-button"
                  onClick={handlePlay}
                >
                  <Play size={32} />
                </button>

                <p className="card-subtitle">
                  Clique e comece sua jornada de edificação
                </p>
              </div>

              <motion.div
                className="pastor-signature"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8, duration: 1.2, ease: "easeOut" }}
              >
                <p>Pastor Cristovão Carriço</p>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Page 1: Jesus Name Animation */}
      <AnimatePresence>
        {currentPage === 1 && (
          <motion.div 
            className="jesus-page"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.h1
              className="jesus-text"
              initial={{ opacity: 0, scale: 0.1 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ 
                duration: 2.5, 
                ease: "easeInOut"
              }}
            >
              JESUS
            </motion.h1>
            <button className="pause-button" onClick={handlePause}>
              <Pause size={24} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

  {/* Page 2: Primeiro Vídeo (logo removida) */}
      <AnimatePresence>
        {currentPage === 2 && (
          <motion.div 
            className="video-page"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="video-wrapper">
              <VideoWithReflections
                videoRef={video1Ref}
                src="/firstvideo.mp4"
                autoPlay
                muted
                playsInline
              />
            </div>
            
            {/* Overlay de logo removido */}
            
            <button className="pause-button" onClick={handlePause}>
              <Pause size={24} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Page 3: Second Video */}
      <AnimatePresence>
        {currentPage === 3 && (
          <motion.div 
            className="video-page"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="video-wrapper">
              <VideoWithReflections
                videoRef={video2Ref}
                src="/20250917-085639-sora.mp4"
                autoPlay
                muted
                playsInline
              />
            </div>
            
            
            <button className="pause-button" onClick={handlePause}>
              <Pause size={24} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Page 4: Final Page with Angelic Background */}
      <AnimatePresence>
        {currentPage === 4 && (
          <div className="final-page">
            <motion.div 
              className="final-jesus-title"
              initial={{ opacity: 0, scale: 0.8, y: 50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 1.2, type: "spring", bounce: 0.4 }}
            >
              <h1 className="final-jesus-text final-jesus-text--giant">JESUS</h1>
              <p className="final-jesus-subtitle">O único Deus</p>
              <p className="final-jesus-lead">Testemunho de vida e Conversão do Pastor Cristovão Carriço</p>
            </motion.div>



            <div className="final-content-layout three-column-layout">
              <motion.div 
                className="side-card left-card"
                initial={{ opacity: 0, x: -100, scale: 0.9 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                transition={{ delay: 2.5, duration: 0.8, type: "spring", bounce: 0.3 }}
              >
                <div className="elevai-card elevai-card--spotify" ref={spotifyCardRef}>
                  <div className="card-glow-layer" />
                  <h3 className="card-heading"><span>Ouça o Testemunho</span><small>no Spotify</small></h3>
                  <p className="card-tagline">Experimente a jornada completa em áudio imersivo</p>
                  <a className="primary-action" href="https://open.spotify.com/episode/1D0UDCnLTjiCOpOMwRKZWZ?si=WtXOqdmSR7qQuaglvCHQcw" target="_blank" rel="noopener noreferrer">Reproduzir agora</a>
                </div>
              </motion.div>
              
              <motion.div 
                className="final-card-center center-logo"
                initial={{ opacity: 0, scale: 0.5, y: 100 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ delay: 1.8, duration: 1.0, type: "spring", bounce: 0.4 }}
              >
                <img src="/novo-logo.png" alt="novo logo" className="final-logo-image" onError={(e)=>{e.currentTarget.src='/20250516_0952_Dourado Reluzente_remix_01jvcntekrekjrn1g95b6ymdt1.png';}} />
                <div className="logo-actions">
                  <button className="action-pill action-pill--podcast-big" onClick={()=>window.open('https://open.spotify.com/episode/1D0UDCnLTjiCOpOMwRKZWZ?si=WtXOqdmSR7qQuaglvCHQcw','_blank')}>PODCAST</button>
                </div>
              </motion.div>
              
              <motion.div 
                className="side-card right-card"
                initial={{ opacity: 0, x: 100, scale: 0.9 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                transition={{ delay: 2.5, duration: 0.8, type: "spring", bounce: 0.3 }}
              >
                <div className="elevai-card elevai-card--article" ref={articleCardRef} role="button" tabIndex={0} onClick={()=>setShowArticleModal(true)} onKeyDown={(e)=>{if(e.key==='Enter') setShowArticleModal(true);}}>
                  <div className="card-glow-layer" />
                  <h3 className="card-heading">Leia o Testemunho Edificante</h3>
                  <p className="card-tagline">Publicado em 17 de Set 25</p>
                  <p className="article-preview-line">Clique para abrir o testemunho completo…</p>
                  <button className="open-article-btn" onClick={()=>setShowArticleModal(true)} aria-label="Abrir testemunho completo">LER COMPLETO</button>
                </div>
              </motion.div>
            </div>

            
            <footer className="mission-footer">
              <p>"Para que o nome de Jesus seja engrandecido sobre toda a terra"</p>
            </footer>
            {showArticleModal && (
              <div className="modal-overlay" onClick={()=>setShowArticleModal(false)}>
                <div className="modal-dialog" onClick={(e)=>e.stopPropagation()} role="dialog" aria-modal="true">
                  <button className="modal-close" onClick={()=>setShowArticleModal(false)} aria-label="Fechar">×</button>
                  <div className="modal-scroll">
                    <article className="article-body full">
                      <p className="publish-date">Publicado em 17 de Set 25</p>
                      <h1>Elevação Através da Fé: O Poder Transformador da Oração</h1>
                      <h2>Da simplicidade do interior à liderança de 6 congregações: uma jornada de crescimento espiritual</h2>
                      <p><em>Episódio especial do Podcast ELEVAI.ME<br/>Por Cristine Carriço</em></p>
                      <hr />
                      <p>Nascido em 1962 no Rio de Janeiro, em uma família humilde mas rica em fé, ele cresceu observando o poder transformador da oração através dos olhos de sua mãe. Uma mulher semianalfabeta, mas com uma fé gigantesca, que durante 30 anos intercedeu pela conversão do marido - e venceu.</p>
                      <p>"Minha mãe nunca começava o dia sem buscar a Deus por cada um dos filhos", relembra. "Ela criou oito filhos vindos do interior do Rio de Janeiro, e sua perseverança em oração foi o que sustentou nossa família."</p>
                      <h3>Os Primeiros Passos</h3>
                      <p>Aos 6 anos, já caminhava três horas a pé, no escuro, para chegar à pequena igreja pentecostal. Aos 11 anos, aceitou Jesus na Igreja Batista, e aos 12, foi batizado. Participava do grupo "Embaixadores do Rei", desenvolvendo suas primeiras habilidades de liderança cristã.</p>
                      <p>"Quando me apresentei no Exército aos 18 anos, eu já tinha disciplina e obediência - valores aprendidos na igreja", conta. "Servi um ano e saí com honra ao mérito, melhor praça da companhia."</p>
                      <h3>O Desvio</h3>
                      <p>O casamento aos 21 anos marcou o início de um período turbulento. Casado com uma jovem católica, gradualmente se afastou da fé. Por mais de 10 anos, viveu longe da igreja, enfrentando brigas constantes no casamento e situações que quase lhe custaram a vida.</p>
                      <p>"Quando você está desviado, você se torna um canal do inimigo", reflete. "O diabo tentou me matar quase quatro vezes - acidentes de carro, envolvimento involuntário com a violência do bairro. Mas Jesus não permitiu."</p>
                      <h3>A Restauração</h3>
                      <p>Em 1993-94, algo mudou. Sua esposa Janete, inspirada pelas irmãs de oração do bairro, começou a buscar a Deus intensamente. Sua mãe, mais uma vez, entrou em intercessão.</p>
                      <p>"Foi nesse período que Deus começou a me tratar", lembra. "Tive uma semana inteira de sonhos sobre o fim do mundo, Jesus voltando, e eu ficando para trás porque não estava servindo como devia."</p>
                      <p>O processo não foi fácil. Deus permitiu que perdesse seus bens materiais - três automóveis, emprego, segurança financeira. O que parecia destruição era reconstrução.</p>
                      <p>"Tinha três carros, perdi tudo. Ganhava dez salários mínimos, perdi o emprego. Mas ganhei a coisa mais valiosa do universo: minha alma de volta", testemunha emocionado.</p>
                      <h3>Tragédia e Chamado</h3>
                      <p>Em abril de 1996, sua mãe faleceu. Em julho, seu pai também partiu. Órfão aos 34 anos, enfrentou o momento mais difícil, mas foi quando o chamado pastoral se tornou inegável.</p>
                      <p>"Perder os pais foi como perder um pedaço de mim. Mas tinha ao meu lado minha esposa e filhos, que já buscavam ao Senhor. Era hora de assumir meu chamado."</p>
                      <h3>Novo Nascimento</h3>
                      <p>Em 5 de julho de 1997, aos 35 anos, foi batizado em nome de Jesus na Igreja Casa de Oração Betel, onde permanece há 29 anos. Deus lhe concedeu dons espirituais - profecia, discernimento, revelação e cura.</p>
                      <p>Em 10 de agosto de 1997, fundou o Ministério Casa de Oração Betel no Rio de Janeiro. Viu Deus transformar sua família completa. Seus filhos se formaram, o mais velho vive no Canadá há oito anos, e sua filha se tornou administradora.</p>
                      <h3>O Teste Final</h3>
                      <p>Em 2022, após 42 anos de casamento, sua esposa Janete contraiu COVID-19. Após dois anos de luta, ela faleceu em seus braços em 27 de julho.</p>
                      <p>"Não reclamei do Senhor. Todas as coisas cooperam para o bem daqueles que amam a Deus. Minha esposa cumpriu sua missão e Deus a recolheu."</p>
                      <h3>Hoje</h3>
                      <p>Aos 63 anos, é pastor dirigente responsável por congregações no Rio de Janeiro, Minas Gerais, Piauí e Rio Grande do Sul - seis congregações ao todo. Consagrado pastor em janeiro de 2024, continua servindo com a mesma paixão.</p>
                      <h3>Lições</h3>
                      <p><strong>Sobre Perseverança</strong>: "Minha mãe orou 30 anos pela conversão do meu pai. A oração perseverante sempre vence."</p>
                      <p><strong>Sobre o Chamado</strong>: "Quando você tem um chamado de Deus, não lute contra ele. Escolhido você já é."</p>
                      <p><strong>Sobre as Provas</strong>: "Tudo compensa. Tenho vivido a palavra: 'Deleita-te no Senhor e Ele satisfará o desejo do teu coração.'"</p>
                      <h3>Mensagem Final</h3>
                      <p>"Nunca é tarde para voltar", é sua mensagem. "Se você está fora da comunhão, volta, porque Jesus está voltando. Deixa Deus entrar no seu coração."</p>
                      <p>Sua história prova que não importa quão longe alguém se desvie, o amor de Deus é maior. De um jovem desviado que quase perdeu tudo, Deus fez um pastor que impacta vidas em quatro estados brasileiros.</p>
                      <p>"Que Jesus cresça e que eu diminua. Que eu seja lâmpada e Ele seja o óleo que brilha", conclui com humildade.</p>
                      <p>Uma vida que começou na simplicidade, passou pelo vale da sombra e floresceu em ministério frutífero - provando que nas mãos de Deus, toda história pode ter um final redentor.</p>
                    </article>
                  </div>
                </div>
              </div>
            )}
            
            <button className="pause-button" onClick={handlePause}>
              <Pause size={24} />
            </button>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default App;
